import React, { useState } from 'react';
import {
  BarChart3,
  Link as LinkIcon,
  MapPin,
  Music,
  Smile,
  HelpCircle,
  Camera,
  ChevronLeft,
  ChevronRight,
  X,
  Send,
  Heart,
} from 'lucide-react';

type StickerType = 'poll' | 'question' | 'quiz' | 'slider' | 'link' | 'location' | 'music' | 'mention' | 'countdown';

interface StoryFrame {
  index: number;
  image: string;
  headline: string;
  copy: string;
  stickers: {
    type: StickerType;
    label: string;
    detail?: string;
  }[];
  linkDestination?: string;
  textColor?: 'light' | 'dark';
}

interface StoryDay {
  day: string;
  date: string;
  theme: string;
  narrative: string;
  frames: StoryFrame[];
}

// All imagery is real high-resolution photography (Unsplash, free editorial license)
const storyDays: StoryDay[] = [
  {
    day: 'Monday',
    date: 'Week 1 · Day 1',
    theme: 'Anchor Drop — Riviera Morning',
    narrative:
      'Soft-launch the week with a slow-build POV: dawn on deck, anchor down, the kind of swim only guests get. Ends with a poll to learn what the audience wants more of.',
    frames: [
      {
        index: 1,
        image:
          'https://images.unsplash.com/photo-1599666433232-95036179624a?auto=format&fit=crop&w=1080&q=85',
        headline: 'good morning, ionian',
        copy: '6:42 AM · Sazan Island\nThe sea is still asleep.',
        stickers: [
          { type: 'location', label: 'Sazan Island, Albania' },
          { type: 'music', label: 'Lofi — "Slow Coast" by Mell-ø' },
        ],
        textColor: 'light',
      },
      {
        index: 2,
        image:
          'https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=1080&q=85',
        headline: 'anchor down',
        copy: 'Today\'s only agenda:\nswim → breakfast → repeat',
        stickers: [
          { type: 'poll', label: 'This or That:', detail: 'Riviera ☀️  /  Alps 🏔️' },
        ],
        textColor: 'light',
      },
      {
        index: 3,
        image:
          'https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=1080&q=85',
        headline: 'private cove No. 4',
        copy: 'Most of our charters stop here.\nNone of the cruise ships can.',
        stickers: [
          { type: 'link', label: 'See Yacht Charters', detail: '/yacht-charters' },
          { type: 'mention', label: '@zeratravel' },
        ],
        linkDestination: '/yacht-charters',
        textColor: 'light',
      },
      {
        index: 4,
        image:
          'https://images.unsplash.com/photo-1530541930197-ff16ac917b0e?auto=format&fit=crop&w=1080&q=85',
        headline: 'help us plan the week',
        copy: 'Which thread should we pull on next?',
        stickers: [
          {
            type: 'poll',
            label: 'What do you want to see more of?',
            detail: 'Villas  /  Yachts  /  Food  /  Mountains',
          },
        ],
        textColor: 'light',
      },
    ],
  },
  {
    day: 'Tuesday',
    date: 'Week 1 · Day 2',
    theme: 'Inside the Villa',
    narrative:
      'A slow architectural tour of one of our signature villas — Cap Rodon. Use the slider sticker to gauge "dream factor" and finish with a link sticker straight to the villa page.',
    frames: [
      {
        index: 1,
        image:
          'https://images.unsplash.com/photo-1582610116397-edb318620f90?auto=format&fit=crop&w=1080&q=85',
        headline: 'meet villa cap rodon',
        copy: 'Six bedrooms.\nOne olive grove.\nZero neighbors.',
        stickers: [
          { type: 'location', label: 'Cap Rodon, Albania' },
        ],
        textColor: 'light',
      },
      {
        index: 2,
        image:
          'https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=1080&q=85',
        headline: 'the terrace',
        copy: 'Where dinners go long\nand the wifi goes quiet.',
        stickers: [
          { type: 'slider', label: 'Dream factor:', detail: '😍 emoji slider' },
        ],
        textColor: 'light',
      },
      {
        index: 3,
        image:
          'https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&w=1080&q=85',
        headline: 'the pool',
        copy: 'Infinity edge.\nIonian on the horizon.\nNo filter applied.',
        stickers: [
          { type: 'quiz', label: 'Quiz: How many bedrooms?', detail: '4 · 5 · 6 · 7' },
        ],
        textColor: 'light',
      },
      {
        index: 4,
        image:
          'https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&w=1080&q=85',
        headline: 'available aug 14–21',
        copy: 'One window left this summer.\nTap to claim it.',
        stickers: [
          { type: 'link', label: 'Reserve Villa Cap Rodon', detail: '/villa-rentals' },
          { type: 'countdown', label: 'Booking closes Friday 8pm' },
        ],
        linkDestination: '/villa-rentals',
        textColor: 'light',
      },
    ],
  },
  {
    day: 'Wednesday',
    date: 'Week 1 · Day 3',
    theme: 'Berat at Blue Hour',
    narrative:
      'Behind-the-scenes of our Reel shoot. Show the city waking up, the gear, the local guide. Use a question sticker to crowdsource the next city to feature.',
    frames: [
      {
        index: 1,
        image:
          'https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?auto=format&fit=crop&w=1080&q=85',
        headline: 'today, we filmed berat',
        copy: 'The city of a thousand windows.\nAll of them watching back.',
        stickers: [
          { type: 'location', label: 'Berat, Albania' },
          { type: 'music', label: 'Albanian iso-polyphony (UNESCO archive)' },
        ],
        textColor: 'light',
      },
      {
        index: 2,
        image:
          'https://images.unsplash.com/photo-1601581875039-e899893d520c?auto=format&fit=crop&w=1080&q=85',
        headline: 'behind the lens',
        copy: 'Our guide Ardit, 4th-generation Berati.\nHe knows every staircase.',
        stickers: [
          { type: 'mention', label: '@ardit.guides' },
        ],
        textColor: 'light',
      },
      {
        index: 3,
        image:
          'https://images.unsplash.com/photo-1542856391-010fb87dcfed?auto=format&fit=crop&w=1080&q=85',
        headline: 'blue hour, 7:48pm',
        copy: 'For 14 minutes a day,\nBerat turns into a painting.',
        stickers: [
          { type: 'quiz', label: 'How old is Berat Castle?', detail: '800 · 1,400 · 2,400 yrs' },
        ],
        textColor: 'light',
      },
      {
        index: 4,
        image:
          'https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?auto=format&fit=crop&w=1080&q=85',
        headline: 'where to next?',
        copy: 'Help us pick the next city we feature.',
        stickers: [
          {
            type: 'question',
            label: 'Drop a city we should film next:',
            detail: 'Open-text question sticker',
          },
          { type: 'link', label: 'Read our Berat guide', detail: '/travel-blog' },
        ],
        linkDestination: '/travel-blog',
        textColor: 'light',
      },
    ],
  },
  {
    day: 'Thursday',
    date: 'Week 1 · Day 4',
    theme: 'Table for Twelve',
    narrative:
      'Foodie-focused day. Walk the audience from market → kitchen → table. End with a CTA to book the private chef experience.',
    frames: [
      {
        index: 1,
        image:
          'https://images.unsplash.com/photo-1488459716781-31db52582fe9?auto=format&fit=crop&w=1080&q=85',
        headline: 'market run, 7am',
        copy: 'Figs, sea bass, hand-pressed olive oil.\nThe menu writes itself.',
        stickers: [
          { type: 'location', label: 'Pazari i Ri, Tirana' },
        ],
        textColor: 'light',
      },
      {
        index: 2,
        image:
          'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1080&q=85',
        headline: 'chef anila plates up',
        copy: 'Tave kosi.\nAlbanian comfort food, refined.',
        stickers: [
          { type: 'poll', label: 'Tave kosi or fresh fish?', detail: 'Lamb-yogurt  /  Sea bass' },
        ],
        textColor: 'light',
      },
      {
        index: 3,
        image:
          'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1080&q=85',
        headline: 'twelve guests, one long table',
        copy: 'No phones at dinner.\nIt\'s a Zera house rule.',
        stickers: [
          { type: 'slider', label: 'Hungry yet?', detail: '🍽️ slider' },
          { type: 'music', label: '"Sofra" — traditional sofra music' },
        ],
        textColor: 'light',
      },
      {
        index: 4,
        image:
          'https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=1080&q=85',
        headline: 'book the chef',
        copy: 'Private dinners, every itinerary.\nAdd-on starts at €180/guest.',
        stickers: [
          { type: 'link', label: 'Add a Chef Experience', detail: '/booking' },
        ],
        linkDestination: '/booking',
        textColor: 'light',
      },
    ],
  },
  {
    day: 'Friday',
    date: 'Week 1 · Day 5',
    theme: 'Into the Alps',
    narrative:
      'Pivot to adventure. Use this day to widen brand perception — Zera is not just beach. Use a countdown for the autumn hiking season opening.',
    frames: [
      {
        index: 1,
        image:
          'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1080&q=85',
        headline: '6 hours from the coast',
        copy: 'Same country.\nCompletely different planet.',
        stickers: [
          { type: 'location', label: 'Theth National Park' },
        ],
        textColor: 'light',
      },
      {
        index: 2,
        image:
          'https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1080&q=85',
        headline: 'the valbona pass',
        copy: 'Three hours up.\nOne lifetime of view.',
        stickers: [
          { type: 'quiz', label: 'Quiz: highest peak?', detail: 'Korab · Jezerca · Maja e Hekurave' },
        ],
        textColor: 'light',
      },
      {
        index: 3,
        image:
          'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1080&q=85',
        headline: 'guesthouse Nik',
        copy: 'Stone walls, wood fire,\nrakia poured by hand.',
        stickers: [
          { type: 'mention', label: '@guesthouse.nik' },
          { type: 'poll', label: 'Riviera vs Alps?', detail: 'Beach 🌊  /  Peak 🏔️' },
        ],
        textColor: 'light',
      },
      {
        index: 4,
        image:
          'https://images.unsplash.com/photo-1454496522488-7a8e488e8606?auto=format&fit=crop&w=1080&q=85',
        headline: 'autumn season opens',
        copy: 'Golden larches. Cold streams.\nFour bookings left.',
        stickers: [
          { type: 'countdown', label: 'Hiking season opens Sept 1' },
          { type: 'link', label: 'Plan an Alpine Itinerary', detail: '/corporate-travel' },
        ],
        linkDestination: '/corporate-travel',
        textColor: 'light',
      },
    ],
  },
  {
    day: 'Saturday',
    date: 'Week 1 · Day 6',
    theme: 'Butrint — A Walking Story',
    narrative:
      'Saturday is for slow scrollers. A mini-documentary in 5 frames. End with a save-this prompt and a link to the heritage tour.',
    frames: [
      {
        index: 1,
        image:
          'https://images.unsplash.com/photo-1555992336-fb0d29498b13?auto=format&fit=crop&w=1080&q=85',
        headline: '2,500 years in one walk',
        copy: 'Greek. Roman. Byzantine. Venetian. Ottoman.\nButrint kept them all.',
        stickers: [
          { type: 'location', label: 'Butrint National Park' },
        ],
        textColor: 'light',
      },
      {
        index: 2,
        image:
          'https://images.unsplash.com/photo-1552832230-c0197dd311b5?auto=format&fit=crop&w=1080&q=85',
        headline: 'the amphitheater',
        copy: 'Still acoustically perfect.\nWe whispered. It carried.',
        stickers: [
          { type: 'music', label: '"Adagio in G Minor" — Albinoni' },
        ],
        textColor: 'light',
      },
      {
        index: 3,
        image:
          'https://images.unsplash.com/photo-1568849676085-51415703900f?auto=format&fit=crop&w=1080&q=85',
        headline: 'the lion gate',
        copy: 'Carved 2,400 years ago.\nStill standing watch.',
        stickers: [
          { type: 'quiz', label: 'Which empire built it?', detail: 'Greek · Roman · Byzantine' },
        ],
        textColor: 'light',
      },
      {
        index: 4,
        image:
          'https://images.unsplash.com/photo-1564507592333-c60657eea523?auto=format&fit=crop&w=1080&q=85',
        headline: 'our private guide',
        copy: 'Dr. Elira spent 11 years\nexcavating this site.',
        stickers: [
          { type: 'mention', label: '@elira.archaeology' },
        ],
        textColor: 'light',
      },
      {
        index: 5,
        image:
          'https://images.unsplash.com/photo-1555992336-fb0d29498b13?auto=format&fit=crop&w=1080&q=85',
        headline: 'save this for later',
        copy: 'Then come walk it with us.',
        stickers: [
          { type: 'link', label: 'Book the Heritage Tour', detail: '/booking' },
          { type: 'question', label: 'Ask us anything about Butrint:' },
        ],
        linkDestination: '/booking',
        textColor: 'light',
      },
    ],
  },
  {
    day: 'Sunday',
    date: 'Week 1 · Day 7',
    theme: 'Sunday Slow — Gjirokastër',
    narrative:
      'Close the week soft. No CTA pressure. Repost guest UGC, share a poem, leave the DMs open. Builds parasocial trust.',
    frames: [
      {
        index: 1,
        image:
          'https://images.unsplash.com/photo-1604357209793-fca5dca89f97?auto=format&fit=crop&w=1080&q=85',
        headline: 'sunday in gjirokastër',
        copy: 'Stone city.\nSlow morning.\nNo plans.',
        stickers: [
          { type: 'location', label: 'Gjirokastër, Albania' },
        ],
        textColor: 'light',
      },
      {
        index: 2,
        image:
          'https://images.unsplash.com/photo-1528127269322-539801943592?auto=format&fit=crop&w=1080&q=85',
        headline: 'a guest sent this in',
        copy: '"We came for a week.\nWe left wanting a different life."\n— Margot & James, July',
        stickers: [
          { type: 'mention', label: '@margotandjames (reposted with permission)' },
        ],
        textColor: 'light',
      },
      {
        index: 3,
        image:
          'https://images.unsplash.com/photo-1519181245277-cffeb31da2e3?auto=format&fit=crop&w=1080&q=85',
        headline: 'a small ritual',
        copy: 'Every Sunday at sunset,\nthe whole town walks. Just walks.\nNo destination.',
        stickers: [
          { type: 'slider', label: 'Mood?', detail: '🌅 emoji slider' },
        ],
        textColor: 'light',
      },
      {
        index: 4,
        image:
          'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1080&q=85',
        headline: 'see you monday',
        copy: 'DMs are open all week.\nWe answer every one.',
        stickers: [
          { type: 'question', label: 'Tell us where you want to go next year:' },
          { type: 'link', label: 'Plan a 2026 Journey', detail: '/' },
        ],
        linkDestination: '/',
        textColor: 'light',
      },
    ],
  },
];

const stickerIcon = (type: StickerType) => {
  switch (type) {
    case 'poll':
      return BarChart3;
    case 'question':
      return HelpCircle;
    case 'quiz':
      return HelpCircle;
    case 'slider':
      return Smile;
    case 'link':
      return LinkIcon;
    case 'location':
      return MapPin;
    case 'music':
      return Music;
    case 'mention':
      return Send;
    case 'countdown':
      return Camera;
    default:
      return Camera;
  }
};

const stickerColor: Record<StickerType, string> = {
  poll: 'bg-[#C9A961]/15 text-[#8B6F47] border-[#C9A961]/40',
  question: 'bg-[#1A3B5C]/10 text-[#1A3B5C] border-[#1A3B5C]/30',
  quiz: 'bg-rose-50 text-rose-700 border-rose-200',
  slider: 'bg-amber-50 text-amber-800 border-amber-200',
  link: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  location: 'bg-sky-50 text-sky-800 border-sky-200',
  music: 'bg-violet-50 text-violet-800 border-violet-200',
  mention: 'bg-[#F3EFE6] text-[#1A3B5C] border-[#C9A961]/30',
  countdown: 'bg-orange-50 text-orange-800 border-orange-200',
};

const StoryFrameMockup: React.FC<{ frame: StoryFrame; total: number }> = ({ frame, total }) => {
  return (
    <div className="relative mx-auto" style={{ width: 260 }}>
      {/* 9:16 phone-frame mockup */}
      <div className="relative rounded-[36px] overflow-hidden shadow-2xl shadow-[#0D1B2A]/30 border-[6px] border-[#0D1B2A] bg-black" style={{ aspectRatio: '9 / 16' }}>
        <img
          src={frame.image}
          alt={frame.headline}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Subtle vignette so text reads */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />

        {/* Story progress bars */}
        <div className="absolute top-2 left-2 right-2 flex gap-1">
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} className="flex-1 h-[2px] rounded-full bg-white/30 overflow-hidden">
              <div
                className={`h-full bg-white ${i < frame.index ? 'w-full' : i === frame.index - 1 ? 'w-1/2' : 'w-0'}`}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-5 left-3 right-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A961] to-[#8B6F47] flex items-center justify-center ring-2 ring-white">
              <span className="font-serif text-white text-[10px]">Z</span>
            </div>
            <div className="text-white text-[11px] font-sans font-semibold drop-shadow">zeratravel</div>
            <div className="text-white/70 text-[10px] font-sans">{frame.index}h</div>
          </div>
          <X className="w-4 h-4 text-white/90" />
        </div>

        {/* Headline + copy */}
        <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 text-center">
          <div className="font-serif text-white text-[22px] leading-[1.1] tracking-wide drop-shadow-lg lowercase">
            {frame.headline}
          </div>
          <div className="mt-3 font-sans text-white/95 text-[11px] leading-[1.5] whitespace-pre-line drop-shadow">
            {frame.copy}
          </div>
        </div>

        {/* Sticker representations */}
        <div className="absolute inset-x-4 bottom-16 flex flex-col gap-2 items-center">
          {frame.stickers.map((s, i) => {
            if (s.type === 'poll') {
              return (
                <div key={i} className="w-full bg-white/95 rounded-xl px-3 py-2 shadow-lg">
                  <div className="font-sans text-[10px] text-[#1A3B5C] font-semibold mb-1.5 text-center">{s.label}</div>
                  <div className="flex gap-1.5">
                    {(s.detail || 'Yes  /  No').split('/').map((opt, j) => (
                      <div
                        key={j}
                        className="flex-1 text-center text-[10px] font-sans font-medium py-1 rounded-md bg-[#F3EFE6] text-[#1A3B5C] border border-[#C9A961]/30"
                      >
                        {opt.trim()}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            if (s.type === 'question') {
              return (
                <div key={i} className="w-full bg-white/95 rounded-xl px-3 py-2 shadow-lg text-center">
                  <div className="font-sans text-[9px] uppercase tracking-wider text-[#8B6F47] mb-1">Ask me anything</div>
                  <div className="font-sans text-[11px] text-[#1A3B5C]">{s.label}</div>
                </div>
              );
            }
            if (s.type === 'slider') {
              return (
                <div key={i} className="w-full bg-white/95 rounded-xl px-3 py-2 shadow-lg">
                  <div className="font-sans text-[10px] text-[#1A3B5C] text-center mb-1">{s.label}</div>
                  <div className="relative h-1.5 bg-[#F3EFE6] rounded-full">
                    <div className="absolute left-0 top-0 h-full w-2/3 bg-gradient-to-r from-[#C9A961] to-[#8B6F47] rounded-full" />
                    <div className="absolute left-2/3 top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-white rounded-full shadow border border-[#C9A961]" />
                  </div>
                </div>
              );
            }
            if (s.type === 'link') {
              return (
                <div key={i} className="bg-white/95 rounded-full px-3 py-1.5 shadow-lg flex items-center gap-1.5">
                  <LinkIcon className="w-3 h-3 text-[#1A3B5C]" />
                  <span className="font-sans text-[10px] text-[#1A3B5C] font-semibold">{s.label}</span>
                </div>
              );
            }
            if (s.type === 'location') {
              return (
                <div key={i} className="bg-gradient-to-r from-[#C9A961] to-[#8B6F47] rounded-md px-2 py-1 shadow flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-white" />
                  <span className="font-sans text-[10px] text-white font-bold uppercase tracking-wider">{s.label}</span>
                </div>
              );
            }
            if (s.type === 'music') {
              return (
                <div key={i} className="bg-black/60 backdrop-blur-sm rounded-full px-2.5 py-1 shadow flex items-center gap-1.5">
                  <Music className="w-3 h-3 text-white" />
                  <span className="font-sans text-[9px] text-white">{s.label}</span>
                </div>
              );
            }
            if (s.type === 'mention') {
              return (
                <div key={i} className="bg-white/95 rounded-md px-2 py-1 shadow">
                  <span className="font-sans text-[10px] text-[#1A3B5C] font-semibold">{s.label}</span>
                </div>
              );
            }
            if (s.type === 'quiz') {
              return (
                <div key={i} className="w-full bg-white/95 rounded-xl px-3 py-2 shadow-lg">
                  <div className="font-sans text-[10px] text-[#1A3B5C] font-semibold mb-1 text-center">{s.label}</div>
                  <div className="font-sans text-[9px] text-[#8B6F47] text-center">{s.detail}</div>
                </div>
              );
            }
            if (s.type === 'countdown') {
              return (
                <div key={i} className="bg-gradient-to-r from-[#1A3B5C] to-[#0D1B2A] rounded-lg px-3 py-1.5 shadow-lg text-center">
                  <div className="font-sans text-[8px] uppercase tracking-widest text-[#C9A961]">Countdown</div>
                  <div className="font-sans text-[10px] text-white font-semibold">{s.label}</div>
                </div>
              );
            }
            return null;
          })}
        </div>

        {/* Reply bar */}
        <div className="absolute bottom-3 inset-x-3 flex items-center gap-2">
          <div className="flex-1 rounded-full border border-white/50 px-3 py-1.5 text-white/70 text-[10px] font-sans">
            Send message
          </div>
          <Heart className="w-4 h-4 text-white" />
          <Send className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );
};


interface StoriesPlannerProps {
  /** Optional override loaded from the database. When omitted or empty, the static seed plan is used. */
  dynamicDays?: StoryDay[];
}

const StoriesPlanner: React.FC<StoriesPlannerProps> = ({ dynamicDays }) => {
  const [activeDayIndex, setActiveDayIndex] = useState(0);
  const [activeFrame, setActiveFrame] = useState(0);

  const effectiveDays: StoryDay[] = dynamicDays && dynamicDays.length > 0 ? dynamicDays : storyDays;
  const activeDay = effectiveDays[activeDayIndex] || effectiveDays[0];

  const goPrev = () => setActiveFrame((f) => Math.max(0, f - 1));
  const goNext = () => setActiveFrame((f) => Math.min(activeDay.frames.length - 1, f + 1));

  const selectDay = (i: number) => {
    setActiveDayIndex(i);
    setActiveFrame(0);
  };

  const totalStories = effectiveDays.reduce((sum, d) => sum + d.frames.length, 0);

  return (
    <div>
      {/* Overview strip */}
      <section className="py-12 border-b border-[#C9A961]/20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 grid md:grid-cols-4 gap-8">
          {[
            { label: 'Story Frames This Week', value: String(totalStories) },
            { label: 'Daily Cadence', value: '3 – 5 frames' },
            { label: 'Avg. Watch Time Goal', value: '> 4.5s / frame' },
            { label: 'Primary CTA', value: 'Link Sticker' },
          ].map((stat) => (
            <div key={stat.label} className="text-center md:text-left">
              <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-2">{stat.label}</div>
              <div className="font-serif text-xl text-[#1A3B5C]">{stat.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Day selector */}
      <section className="py-10 bg-[#FAF8F3] border-b border-[#C9A961]/20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-8">
            <span className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#C9A961]">Daily Story Plan</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A3B5C] mt-3 font-light">Seven Days, Frame by Frame</h2>
            <div className="w-12 h-[2px] bg-[#C9A961] mx-auto mt-6" />
          </div>

          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {effectiveDays.map((d, i) => (
              <button
                key={d.day}
                onClick={() => selectDay(i)}
                className={`px-4 py-2.5 border font-sans text-[11px] tracking-[0.2em] uppercase transition-all duration-300 ${
                  i === activeDayIndex
                    ? 'bg-[#1A3B5C] text-white border-[#1A3B5C]'
                    : 'bg-white text-[#1A3B5C] border-[#C9A961]/30 hover:border-[#C9A961] hover:bg-[#F3EFE6]'
                }`}
              >
                {d.day}
                <span className="ml-2 text-[9px] opacity-70">{d.frames.length}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Active day detail */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          {/* Day header */}
          <div className="text-center mb-10">
            <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-2">{activeDay.date}</div>
            <h3 className="font-serif text-4xl md:text-5xl text-[#1A3B5C] font-light">{activeDay.day}</h3>
            <div className="font-sans text-[13px] tracking-[0.2em] uppercase text-[#8B6F47] mt-3">{activeDay.theme}</div>
            <p className="font-sans text-[14px] text-[#1A3B5C]/70 leading-relaxed max-w-2xl mx-auto mt-5 italic">
              {activeDay.narrative}
            </p>
          </div>

          {/* Active frame preview + details */}
          <div className="grid lg:grid-cols-[auto_1fr] gap-12 items-start mb-16">
            {/* Story mockup */}
            <div className="flex flex-col items-center gap-4">
              <StoryFrameMockup frame={activeDay.frames[activeFrame]} total={activeDay.frames.length} />
              <div className="flex items-center gap-3">
                <button
                  onClick={goPrev}
                  disabled={activeFrame === 0}
                  className="w-9 h-9 flex items-center justify-center border border-[#C9A961]/40 text-[#1A3B5C] hover:bg-[#1A3B5C] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#1A3B5C] transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="font-sans text-[11px] tracking-[0.25em] uppercase text-[#1A3B5C]/70">
                  Frame {activeFrame + 1} / {activeDay.frames.length}
                </div>
                <button
                  onClick={goNext}
                  disabled={activeFrame === activeDay.frames.length - 1}
                  className="w-9 h-9 flex items-center justify-center border border-[#C9A961]/40 text-[#1A3B5C] hover:bg-[#1A3B5C] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#1A3B5C] transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Frame details */}
            <div className="space-y-6">
              <div>
                <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-3">Headline</div>
                <h4 className="font-serif text-3xl text-[#1A3B5C] font-light lowercase">
                  {activeDay.frames[activeFrame].headline}
                </h4>
              </div>

              <div>
                <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-3">On-Screen Copy</div>
                <p className="font-sans text-[15px] text-[#1A3B5C]/85 leading-[1.8] whitespace-pre-line border-l-2 border-[#C9A961] pl-4">
                  {activeDay.frames[activeFrame].copy}
                </p>
              </div>

              <div>
                <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-3">Stickers & Interactions</div>
                <div className="flex flex-wrap gap-2">
                  {activeDay.frames[activeFrame].stickers.map((s, i) => {
                    const Icon = stickerIcon(s.type);
                    return (
                      <div
                        key={i}
                        className={`flex items-start gap-2 px-3 py-2 border rounded-md ${stickerColor[s.type]}`}
                      >
                        <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-sans text-[11px] font-semibold uppercase tracking-wider">
                            {s.type}
                          </div>
                          <div className="font-sans text-[12px]">{s.label}</div>
                          {s.detail && (
                            <div className="font-sans text-[11px] opacity-80 italic mt-0.5">{s.detail}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {activeDay.frames[activeFrame].linkDestination && (
                <div>
                  <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-3">Link Sticker Destination</div>
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#1A3B5C] text-white">
                    <LinkIcon className="w-4 h-4 text-[#C9A961]" />
                    <span className="font-sans text-[13px] tracking-wider">
                      zera.travel{activeDay.frames[activeFrame].linkDestination}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* All frames thumbnail strip */}
          <div>
            <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-4 text-center">
              All {activeDay.frames.length} Frames — {activeDay.day}
            </div>
            <div className="flex justify-center gap-3 flex-wrap">
              {activeDay.frames.map((f, i) => (
                <button
                  key={i}
                  onClick={() => setActiveFrame(i)}
                  className={`relative rounded-lg overflow-hidden transition-all duration-300 ${
                    i === activeFrame
                      ? 'ring-2 ring-[#C9A961] scale-105'
                      : 'ring-1 ring-[#C9A961]/20 opacity-70 hover:opacity-100'
                  }`}
                  style={{ width: 90, aspectRatio: '9 / 16' }}
                >
                  <img src={f.image} alt={f.headline} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
                  <div className="absolute bottom-1 left-1 right-1 font-sans text-[8px] text-white text-center lowercase tracking-wide">
                    {f.headline}
                  </div>
                  <div className="absolute top-1 right-1 bg-black/60 rounded px-1 text-[8px] text-white font-sans">
                    {i + 1}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stories strategy notes */}
      <section className="py-20 bg-[#F3EFE6] border-t border-[#C9A961]/20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#C9A961]">Stories Playbook</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A3B5C] mt-3 font-light">How We Run a Week of Stories</h2>
            <div className="w-12 h-[2px] bg-[#C9A961] mx-auto mt-6" />
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Cadence',
                body: '3–5 frames per day, spaced from late morning to early evening. Never more than 6 — the drop-off curve gets ugly past frame 7.',
              },
              {
                title: 'Frame 1 Rule',
                body: 'Frame one is always our strongest visual + a 4-word hook. If frame one fails, the rest do not get watched.',
              },
              {
                title: 'Interactive Sticker Per Day',
                body: 'Every day must include at least one poll, quiz, slider, or question sticker. We feed the algorithm with taps, and we feed our planning with answers.',
              },
              {
                title: 'Link Sticker Strategy',
                body: 'One link sticker per day, never on frame 1. Land on the most relevant page (villa, charter, booking) — never the homepage unless intentional.',
              },
              {
                title: 'Highlights Routing',
                body: 'Mon → "Yachts" highlight. Tue → "Villas". Wed/Sat → "Culture". Thu → "Table". Fri → "Alps". Sun → "Guests".',
              },
              {
                title: 'Reply Discipline',
                body: 'Every poll vote, question, and DM gets a reply within 4 hours. Concierge tone — first name, no scripts, no emoji walls.',
              },
            ].map((note) => (
              <div key={note.title} className="bg-white border border-[#C9A961]/30 p-8">
                <h3 className="font-serif text-xl text-[#1A3B5C] mb-3">{note.title}</h3>
                <p className="font-sans text-[14px] text-[#1A3B5C]/70 leading-relaxed">{note.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default StoriesPlanner;
